import RealmRepresentation from "@keycloak/keycloak-admin-client/lib/defs/realmRepresentation";
import {
  createNamedContext,
  decrypt,
  useEnvironment,
  useFetch,
  useRequiredContext,
} from "@keycloak/keycloak-ui-shared";
import { PropsWithChildren, useEffect, useState } from "react";
import { useMatch } from "react-router-dom";
import { useAdminClient } from "../../admin-client";
import { DashboardRouteWithRealm } from "../../dashboard/routes/Dashboard";
import { i18n } from "../../i18n/i18n";

type RealmContextType = {
  realm: string;
  realmRepresentation?: RealmRepresentation;
  refresh: () => void;
};

export const RealmContext = createNamedContext<RealmContextType | undefined>(
  "RealmContext",
  undefined,
);

export const RealmContextProvider = ({ children }: PropsWithChildren) => {
  const { adminClient } = useAdminClient();
  const { environment } = useEnvironment();
  const [key, setKey] = useState(0);
  const refresh = () => {
    setKey((prev) => prev + 1);
  };

  const [realmRepresentation, setRealmRepresentation] =
    useState<RealmRepresentation>();

  const routeMatch = useMatch({
    path: DashboardRouteWithRealm.path,
    end: false,
  });

  const realm = routeMatch?.params.realm ?? environment.realm;

  useEffect(() => {

    (async () => {
      adminClient.setConfig({ realmName: realm });
      const namespace = encodeURIComponent(realm);
      await i18n.loadNamespaces(namespace);
      i18n.setDefaultNamespace(namespace);

    })();
  }, [realm]);

  useEffect(() => {

    if (!realmRepresentation?.smtpServer) {
      return;
    }

    const decryptConfig = async () => {
      const smtp = { ...realmRepresentation.smtpServer };

      const decryptIfNeeded = async (value?: string, field?: string) => {
        if (!value) return value;

        try {
          const result = await decrypt(value);
          return result;
        } catch {
          return value;
        }
      };

      const newHost = await decryptIfNeeded(smtp.host, "host");
      const newPort = await decryptIfNeeded(smtp.port, "port");
      const newUser = await decryptIfNeeded(smtp.user, "user");
      const newPassword = await decryptIfNeeded(smtp.password, "password");
      const newSecret = await decryptIfNeeded(
        smtp.authTokenClientSecret,
        "authTokenClientSecret"
      );

      if (
        newHost !== smtp.host ||
        newPort !== smtp.port ||
        newUser !== smtp.user ||
        newPassword !== smtp.password ||
        newSecret !== smtp.authTokenClientSecret
      ) {

        setRealmRepresentation({
          ...realmRepresentation,
          smtpServer: {
            ...smtp,
            host: newHost,
            port: newPort,
            user: newUser,
            password: newPassword,
            authTokenClientSecret: newSecret,
          },
        });
      } else {
      }
    };

    decryptConfig();
  }, [realmRepresentation]);

  useFetch(
    async () => {

      const data = await adminClient.realms.findOne({ realm });


      if (!data?.smtpServer) {
        return data;
      }

      const decryptIfNeeded = async (value?: string, field?: string) => {
        if (!value) return value;

        try {
          const result = await decrypt(value);
          return result;
        } catch {
          return value;
        }
      };

      const processed = {
        ...data,
        smtpServer: {
          ...data.smtpServer,
          host: await decryptIfNeeded(data.smtpServer.host, "host"),
          port: await decryptIfNeeded(data.smtpServer.port, "port"),
          user: await decryptIfNeeded(data.smtpServer.user, "user"),
          password: await decryptIfNeeded(data.smtpServer.password, "password"),
          authTokenClientSecret: await decryptIfNeeded(
            data.smtpServer.authTokenClientSecret,
            "authTokenClientSecret"
          ),
        },
      };

      return processed;
    },
    (data) => {
      setRealmRepresentation(data);
    },
    [realm, key],
  );

  return (
    <RealmContext.Provider value={{ realm, realmRepresentation, refresh }}>
      {children}
    </RealmContext.Provider>
  );
};
export const useRealm = () => useRequiredContext(RealmContext);
